// Alert.jsx
import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle2, AlertTriangle, Info } from 'lucide-react';

const Alert = ({ 
  type = 'info',
  message, 
  title,
  duration = 5000,
  onClose,
  onConfirm,
  onCancel,
  confirmText = 'Conferma',
  cancelText = 'Annulla',
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (duration && type !== 'confirm') {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [duration, type]);

  const handleClose = () => {
    setIsLeaving(true);
    // Attendiamo che l'animazione di uscita sia completata
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300); // 300ms è la durata della nostra animazione
  };

  const variants = {
    info: {
      container: 'bg-light-surface-bright text-light-on-surface dark:bg-dark-surface-bright dark:text-dark-on-surface',
      icon: <Info className="w-5 h-5" />
    },
    success: {
      container: 'bg-light-surface-bright text-light-on-surface dark:bg-dark-surface-bright dark:text-dark-on-surface',
      icon: <CheckCircle2 className="w-5 h-5" />
    },
    warning: {
      container: 'bg-light-tertiary text-light-on-tertiary dark:bg-dark-tertiary dark:text-dark-on-tertiary',
      icon: <AlertTriangle className="w-5 h-5" />
    },
    error: {
      container: 'bg-light-error text-light-on-error dark:bg-dark-error dark:text-dark-on-error',
      icon: <AlertCircle className="w-5 h-5" />
    },
    confirm: {
      container: 'bg-light-surface-bright text-light-on-surface dark:bg-dark-surface-bright dark:text-dark-on-surface',
      icon: <AlertCircle className="w-5 h-5" />
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`
      w-full
      rounded-lg shadow-surface-container
      ${isLeaving ? 'animate-fade-out' : 'animate-fade-in'}
      ${variants[type].container}
      ${className}
    `}>
      <div className="flex items-start gap-3 p-4">
        {/* Add icon */}
        <div className="flex-shrink-0 mt-0.5">
          {variants[type].icon}
        </div>
        
        <div className="flex-1 min-w-0"> {/* Aggiungiamo min-w-0 per gestire meglio il testo lungo */}
          {title && (
            <h3 className="font-semibold mb-1 break-words">
              {title}
            </h3>
          )}
          <p className="text-sm break-words">
            {message}
          </p>
          
          {type === 'confirm' && (
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <button
                onClick={() => {
                  onConfirm?.();
                  setIsVisible(false);
                  onClose?.();
                }}
                className="px-4 py-2 bg-light-primary text-light-on-primary dark:bg-dark-primary dark:text-dark-on-primary rounded-md hover:opacity-90 transition-opacity font-medium"
              >
                {confirmText}
              </button>
              <button
                onClick={() => {
                  onCancel?.();
                  setIsVisible(false);
                  onClose?.();
                }}
                className="px-4 py-2 border border-light-outline dark:border-dark-outline text-light-on-surface dark:text-dark-on-surface rounded-md hover:bg-light-surface-container dark:hover:bg-dark-surface-container transition-colors"
              >
                {cancelText}
              </button>
            </div>
          )}
        </div>
        
        {type !== 'confirm' && (
          <button
            onClick={handleClose}
            className="flex-shrink-0 hover:opacity-80 transition-opacity p-1" 
          >
            <X size={20} />
            <span className="sr-only">Close alert</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default Alert;